/* @flow */

import * as React from 'react'
import styled from 'styled-components'

import MobileNavigation from '../../../../infrastructure/components/Mobile/MobileNavigation'
import TopNavigation, { NavigationLinkCss } from './TopNavigation'

const BrandNavigation = ({ ...rest }) => {
  return (
    <TopNavigation createNavigationLinks={createNavigationLinks} {...rest} />
  )
}

export default BrandNavigation

const BrandNavigationSearchButton = styled.button.attrs({
  type: 'button',
})`
  ${NavigationLinkCss};
  background: transparent;
  border: 0;
  font-size: ${({ useNewColors }) => (useNewColors ? '14px' : '18px')};
  outline: 0;
`

const createNavigationLinks = ({
  customer,
  entity,
  onHideMobileNavigation,
  toggleSearch,
  user,
  useNewColors,
}) => {
  const navigationItems = [
    {
      permission: 'dashboard',
      key: 'home',
      link: '/',
      icon: 'lnr lnr-home',
      mobile_label: 'Dashboard',
    },
    {
      key: 'search',
      render: () => {
        return (
          <MainNavigationSearchButton
            className="active"
            onClick={toggleSearch}
            tabIndex="-1"
            useNewColors={useNewColors}
          >
            <span
              className="lnr lnr-magnifier"
              style={{ paddingRight: 0 }}
            ></span>
          </MainNavigationSearchButton>
        )
      },
      render_mobile: () => {
        return (
          <MobileNavigation.FakeLink
            onClick={() => {
              toggleSearch()
              onHideMobileNavigation()
            }}
          >
            Search
          </MobileNavigation.FakeLink>
        )
      },
      icon: 'lnr lnr-home',
    },
    {
      key: 'orders',
      link: '/orders',
      icon: 'file-empty',
      label: 'Sales',
      permission: 'orders:read',

      sub_items: [
        {
          app: ['Shopify', 'Woocommerce'],
          app_not: true,
          link: '/orders',
          label: 'Order List',
          permission: 'orders:read',
        },
        {
          app: ['Shopify', 'Woocommerce'],
          link: '/orders/b2b',
          label: 'B2B Orders',
          permission: 'orders:read',
        },
        {
          app: ['Shopify', 'Woocommerce'],
          link: '/orders/b2c',
          label: 'B2C Orders',
          permission: 'orders:read',
        },
        {
          link: '/invoices',
          label: 'Invoices',
          permission: 'orders:invoices:read',
        },
        {
          link: '/prepayments',
          label: 'Prepayments',
          permission: 'orders:prepayments:read',
        },
        {
          link: '/orders/drops',
          label: 'Drops',
          permission: 'orders:drops:read',
        },
        {
          link: '/returns',
          label: 'Returns',
          permission: 'orders:returns:read',
        },
        {
          link: '/showrooms',
          label: 'Showrooms',
          permission: 'showrooms:read',
          freemium: false,
        },
        {
          link: '/campaigns',
          label: 'Linesheets',
          permission: 'linesheets:read',
        },
        {
          link: '/webshop-carts',
          label: 'Webshop carts',
          permission: 'orders:read',
          new_label: true,
        },
      ],
    },
    {
      key: 'shipments',
      link: '/fulfilment',
      icon: 'location',
      label: 'Fulfilment',
      permission: 'orders:shipments:read',
      app: 'Shipments',

      sub_items: [
        {
          link: '/fulfilment',
          label: 'Dashboard',
          permission: 'orders:shipments:read',
        },
        {
          link: '/shipments/planner',
          label: 'Shipping planner',
          permission: 'orders:shipments:read',
          new_label: true,
          feature_flag: 'shipping_planner',
        },
        {
          link: '/shipments/list',
          label: 'Shipments',
          permission: 'orders:shipments:read',
        },
      ],
    },
    {
      key: 'webshop',
      link: `/shop/${entity.id}`,
      icon: 'cart',
      label: 'Webshop',
      permission: 'webshop:browse',
      freemium: false,
    },
    {
      key: 'customers',
      link: '/customers',
      icon: 'users',
      label: 'Customers',
      permission: 'customers:basic:read',

      sub_items: [
        {
          link: '/customers',
          label: 'Customer List',
          permission: 'customers:basic:read',
        },
        {
          link: '/customer-groups',
          label: 'Customer Groups',
          permission: 'customers:groups:read',
          freemium: false,
        },
        {
          link: '/price-lists',
          label: 'Price Lists',
          permission: 'customers:price-lists:read',
        },
        {
          link: '/customers/import',
          label: 'Import customers',
          permission: 'customers:importer',
          custom_check: ({ entity, customer }) => {
            return (
              entity.freemium ||
              (customer ? customer.customers_importer : false)
            )
          },
        },
      ],
    },
    {
      key: 'inventory',
      link: '/inventory',
      icon: 'layers',
      label: 'Inventory',
      permission: 'inventory:read',

      sub_items: [
        {
          link: '/inventory',
          label: 'Inventory list',
          permission: 'inventory:read',
        },
        {
          link: '/consignment',
          label: 'Consignment',
          permission: 'consignment:read',
        },
        {
          link: '/inventory/reservations',
          label: 'Reservations',
          permission: 'inventory:write',
          agent: false,
          feature_flag: 'reservations_v4',
          feature_flag_not: true,
        },
        {
          link: '/inventory/report',
          label: 'Inventory report',
          permission: 'inventory:write',
          agent: false,
          feature_flag: 'inventory_report',
        },
        {
          link: '/inventory/stock-counts',
          label: 'Stock count',
          permission: 'stock_count:read',
        },
        {
          link: '/inventory/import',
          label: 'Import inventory',
          permission: 'inventory:importer',
          custom_check: ({ entity, customer }) => {
            return (
              entity.freemium ||
              (customer ? customer.inventory_importer : false)
            )
          },
        },
      ],
    },
    {
      key: 'products',
      link: '/products',
      icon: 'shirt',
      label: 'Products',
      permission: 'products:read',

      sub_items: [
        {
          link: '/products',
          label: 'Product List',
          permission: 'products:read',
        },
        {
          link: '/product-groups',
          label: 'Product Groups',
          permission: 'categories:read',
        },
        {
          link: '/subbrands',
          label: 'Brands',
          permission: 'subbrands:read',
        },
        {
          link: '/categories',
          label: 'Categories',
          permission: 'categories:read',
        },
        {
          link: '/collections',
          label: 'Collections',
          permission: 'collections:read',
        },
        {
          link: '/segmentation',
          label: 'Segmentation',
          permission: 'segmentation:read',
        },
        {
          link: '/products/import',
          label: 'Import products',
          permission: 'products:importer',
          custom_check: ({ entity, customer }) => {
            return (
              entity.freemium || (customer ? customer.products_importer : false)
            )
          },
        },
        {
          link: '/launch-events',
          label: 'Launch events',
          feature_flag: 'launch_events',
        },
        {
          link: '/products/images-import',
          label: 'Image Import',
        },
      ],
    },
    {
      key: 'production',
      link: '/production/orders',
      icon: 'pencil',
      label: 'Production',
      permission: 'production:orders:read',
      app: 'Production',

      sub_items: [
        {
          link: '/production',
          label: 'Dashboard',
          permission: 'production:orders:read',
        },
        {
          link: '/production/orders',
          label: 'Production orders',
          permission: 'production:orders:read',
        },
        {
          link: '/production/orders/delivery-notes',
          label: 'Deliveries',
          permission: 'production:delivery_notes:read',
        },
        {
          link: '/production/production-prepayments',
          label: 'Prepayments',
          permission: 'production:prepayments:read',
          new_label: true,
          feature_flag: 'test_production_accounting_v2',
        },
        {
          link: '/production/overhead-cost-invoices',
          label: 'Overhead cost invoices',
          permission: 'production:overhead_cost_invoices:read',
          new_label: true,
          feature_flag: 'test_production_accounting_v2',
        },
        {
          link: '/production/delivery-chart',
          label: 'Delivery chart',
          permission: 'production:delivery_notes:read',
        },
        {
          link: '/production/suppliers',
          label: 'Suppliers',
          permission: 'production:suppliers:read',
        },
      ],
    },
    {
      key: 'reports',
      link: '/reports/revenue',
      icon: 'chart-bars',
      label: 'Reports',
      permission: 'reports:browse',

      sub_items: [
        {
          link: '/reports',
          label: 'Report Generator (BETA)',
          permission: 'reports:browse',
          feature_flag: 'remove_legacy_reports',
          feature_flag_not: true,
        },
        {
          link: '/reports/order_count',
          label: 'Order Count',
          feature_flag: 'remove_legacy_reports',
          feature_flag_not: true,
        },
        {
          link: '/reports/order_totals',
          label: 'Order Totals',
          feature_flag: 'remove_legacy_reports',
          feature_flag_not: true,
        },
        {
          link: '/reports/revenue',
          label: 'Revenue',
        },
        {
          link: '/reports/rankings',
          label: 'Top Rankings',
        },
        {
          link: '/reports/claims',
          label: 'Claims',
        },
        {
          link: '/reports/sales-goals',
          label: 'Sales Goals',
          permission: 'reports:sales-goals:read',
        },
        {
          link: '/reports/warehousing',
          label: 'Warehousing',
          agent: false,
        },
        {
          link: '/reports/cancellations',
          label: 'Cancellations',
          agent: false,
        },
        {
          link: '/reports/unpaid-invoices',
          label: 'Unpaid invoices',
          new_label: true,
          permission: 'unpaid_invoices:read',
        },
        {
          link: '/reports/intrastat',
          label: 'Intrastat',
          agent: false,
        },
        {
          link: '/reports/one-stop-shop',
          label: 'One stop shop',
          new_label: true,
          agent: false,
        },
        {
          link: '/reports/commission',
          label: 'Commission',
          permission: 'customers:commission-reports:read',
        },
      ],
    },
    {
      key: 'app_store',
      link: '/apps',
      icon: 'rocket',
      label: 'App Store',
      permission: 'apps:read',

      sub_items: [
        {
          link: '/apps/riverty',
          label: 'Riverty',
          app: 'Riverty',
        },
        {
          link: '/apps/economic',
          label: 'e-conomic',
          app: 'Economic',
        },
        {
          link: '/apps/exact',
          label: 'Exact',
          app: 'Exact',
        },
        {
          link: '/apps/fortnox',
          label: 'Fortnox',
          app: 'Fortnox',
        },
        {
          link: '/campaigns',
          label: 'Linesheets (Campaigns)',
          permission: 'linesheets:read',
        },
        {
          link: `/file-bank/${entity.id}`,
          label: 'File Bank preview',
          app: 'File Bank',
          permission: 'filebank:read',
        },
        {
          link: `/apps/file-bank`,
          label: 'File Bank settings',
          app: 'File Bank',
          permission: 'filebank:read',
        },
        {
          link: '/apps/create2stay',
          label: 'create2STAY',
          app: 'Create2Stay',
        },
        {
          link: '/apps/shipments',
          label: 'Shipments',
          app: 'Shipments',
        },
        {
          link: '/apps/woocommerce',
          label: 'Woocommerce',
          app: 'Woocommerce',
        },
        {
          link: '/apps/shopify',
          label: 'Shopify',
          app: 'Shopify',
        },
        {
          link: '/apps/data-export',
          label: 'Data export',
          app: 'DataExport',
        },
        {
          link: '/apps/mailchimp',
          label: 'Mailchimp',
          app: 'Mailchimp',
        },
        {
          link: '/apps/production',
          label: 'Production',
          app: 'Production',
        },
        {
          link: '/apps/eaccounting',
          label: 'Eaccounting',
          app: 'eAccounting',
        },
        {
          link: '/apps/thetis-pack',
          label: 'Thetis Pack',
          app: 'ThetisPack',
        },
        {
          link: '/apps/dsv',
          label: 'DSV',
          app: 'Dsv',
        },
        {
          link: '/apps/scanglobal',
          label: 'Scan Global',
          app: 'Scanglobal',
        },
        {
          link: '/apps/rhenus',
          label: 'RHENUS',
          app: 'Rhenus',
        },
        {
          link: '/apps/tmc',
          label: 'TMC',
          app: 'Tmc',
        },
        {
          link: '/apps/dnb',
          label: 'DNB',
          app: 'Dnb',
        },
        {
          link: '/apps/edi',
          label: 'EDI',
          app: 'Edi',
        },
        {
          link: '/apps/hlt',
          label: 'HLT',
          app: 'Hlt',
        },
        {
          link: '/apps/lector',
          label: 'Lector',
          app: 'Lector',
        },
        {
          link: '/apps/pos',
          label: 'POS',
          app: 'GenericPos',
        },
        {
          link: '/apps/business-central',
          label: 'Business Central',
          app: 'BusinessCentral',
        },
        {
          link: '/apps/tripletex',
          label: 'Tripletex',
          app: 'Tripletex',
        },
        {
          link: '/apps/xero',
          label: 'Xero',
          app: 'Xero',
        },
        {
          link: '/apps/quickbooks',
          label: 'Quickbooks',
          app: 'Quickbooks',
        },
        {
          link: '/apps/webshipper',
          label: 'Webshipper',
          app: 'Webshipper',
        },
        {
          link: '/apps/zalando',
          label: 'Zalando',
          app: 'Zalando',
        },
        {
          link: '/apps/faire',
          label: 'Faire',
          app: 'Faire',
        },
        {
          link: '/apps/sendcloud',
          label: 'Sendcloud',
          app: 'Sendcloud',
        },
        {
          link: '/apps/shipmondo',
          label: 'Shipmondo',
          app: 'Shipmondo',
        },
        {
          link: '/apps/3pl',
          label: '3PL',
          app: 'Generic3pl',
        },
        {
          link: '/apps/ongoing',
          label: 'Ongoing',
          app: 'Ongoing',
        },
        {
          link: '/apps/pakkecenternord',
          label: 'PakkecenterNord',
          app: 'PakkecenterNord',
        },
        {
          link: '/apps/picqer',
          label: 'Picqer',
          app: 'Picqer',
        },
        {
          link: '/apps/klaviyo',
          label: 'Klaviyo',
          app: 'Klaviyo',
        },
        {
          link: '/apps/delogue',
          label: 'Delogue',
          app: 'Delogue',
        },
        {
          link: '/apps/systemtransport',
          label: 'Systemtransport',
          app: 'Systemtransport',
        },
        {
          link: '/apps/midtfactoring',
          label: 'Midt Factoring',
          app: 'MidtFactoring',
        },
        {
          link: '/apps/svea',
          label: 'Svea',
          app: 'Svea',
        },
        {
          link: '/apps/epay',
          label: 'ePay',
          app: 'Epay',
        },
        {
          link: '/apps/quickpay',
          label: 'Quickpay',
          app: 'Quickpay',
        },
        {
          link: '/document-exchange',
          label: 'Document exchange',
          permission: 'document-exchange:read',
        },
      ],
    },
    {
      key: 'admin',
      link: '/admin',
      icon: 'cog',
      label: 'Admin',
      admin: true,

      sub_items: [
        {
          link: '/admin/users',
          label: 'Users',
        },
        {
          link: '/admin/paying-customers',
          label: 'Paying Customers',
        },
        {
          link: '/admin/feature-flags',
          label: 'Feature flags',
        },
        {
          link: '/admin/agents',
          label: 'Agents',
        },
        {
          link: '/admin/queue',
          label: 'Queue',
        },
        {
          link: '/admin/imports',
          label: 'Imports',
        },
        {
          link: '/admin/setting-changes',
          label: 'Setting changes',
        },
        {
          link: '/admin/news',
          label: 'News',
        },
        {
          link: '/admin/reports',
          label: 'Reports',
        },
        {
          link: '/admin/miscellaneous-tools',
          label: 'Miscellaneous Tools',
        },
      ],
    },

    // FREMIUM NAVIGATION

    {
      key: 'showrooms',
      link: '/showrooms',
      icon: 'license',
      label: 'Showrooms',
      permission: 'showrooms:browse',
      freemium: true,
    },
    {
      key: 'file-bank',
      link: '/apps/file-bank',
      icon: 'picture',
      label: 'File Bank',
      permission: 'filebank:read',
      freemium: true,

      sub_items: [
        {
          link: `/file-bank/${entity.id}`,
          label: 'Visit',
        },
        {
          link: '/apps/file-bank',
          label: 'Edit',
          permission: 'filebank:write',
        },
      ],
    },

    /*
      When user does not have access to App Store, we might want to at
      least show file bank, linesheets or document exchange separately.
    */
    {
      key: 'file-bank',
      link: `/file-bank/${entity.id}`,
      label: 'File Bank',
      permission: 'filebank:read',
      permission_not: 'apps:read',
      freemium: false,
    },
    {
      key: 'campaigns',
      link: '/campaigns',
      label: 'Campaigns',
      permission: 'linesheets:read',
      permission_not: 'apps:read',
      freemium: false,
    },
  ]

  return navigationItems
}

const MainNavigationSearchButton = styled.button.attrs({
  type: 'button',
})`
  ${NavigationLinkCss};
  background: transparent;
  border: 0;
  font-size: ${({ useNewColors }) => (useNewColors ? '14px' : '18px')};
  outline: 0;
`
